import { useState } from "react";
import { UserPlus, Plus, Pencil, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { useCrudList, crudCreate, crudUpdate, crudDelete } from "@/lib/crud";
import { ROLE_LABELS } from "@/config/navigation";
import { useAuth } from "@/context/AuthContext";

const ROLE_OPTS = [
  { value: "admin", label: "Administrador" },
  { value: "recepcionista", label: "Recepcionista" },
  { value: "terapeuta", label: "Terapeuta" },
  { value: "financeiro", label: "Financeiro" },
];

const empty = { email: "", name: "", password: "", role: "recepcionista", is_active: true, data_nasc: "" };

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  const { items, loading, setItems } = useCrudList("/users");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, password: "", role: u.role, is_active: u.is_active, data_nasc: u.data_nasc || "" });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) {
        const payload = { email: form.email.trim().toLowerCase(), name: form.name, role: form.role, is_active: form.is_active, data_nasc: form.data_nasc || null };
        if (form.password) payload.password = form.password;
        const upd = await crudUpdate("/users", editing.id, payload);
        setItems((prev) => prev.map((x) => x.id === upd.id ? upd : x));
        toast.success("Usuário atualizado");
      } else {
        const c = await crudCreate("/users", { ...form, data_nasc: form.data_nasc || null });
        setItems((prev) => [c, ...prev]);
        toast.success("Usuário cadastrado");
      }
      setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await crudDelete("/users", deleteId);
      setItems((prev) => prev.filter((x) => x.id !== deleteId));
      toast.success("Usuário excluído");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setDeleteId(null); }
  };

  return (
    <div data-testid="usuarios-page">
      <PageHeader
        icon={UserPlus}
        title="Usuários"
        description="Gestão de funcionários e seus níveis de acesso."
        actions={<Button onClick={openNew} data-testid="usuario-new-btn"><Plus className="h-4 w-4 mr-1" />Novo usuário</Button>}
      />

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Carregando…</div>
          ) : items.length === 0 ? (
            <div className="p-6"><EmptyState icon={Shield} title="Nenhum usuário" description="Cadastre os funcionários que terão acesso ao sistema." /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nome</TableHead><TableHead>Email</TableHead>
                <TableHead>Perfil</TableHead><TableHead>Status</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((u) => (
                  <TableRow key={u.id} data-testid={`usuario-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}{u.id === currentUser?.id && <Badge variant="outline" className="ml-2">Você</Badge>}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant="secondary">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                    <TableCell><Badge variant={u.is_active ? "secondary" : "outline"}>{u.is_active ? "Ativo" : "Inativo"}</Badge></TableCell>
                    <TableCell><div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                      {u.id !== currentUser?.id && (
                        <Button size="icon" variant="ghost" onClick={() => setDeleteId(u.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      )}
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle>
            <DialogDescription>{editing ? "Deixe a senha em branco para mantê-la." : "Uma senha inicial será definida agora."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <F label="Nome" req><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="usuario-form-nome" /></F>
            <F label="Email" req><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="usuario-form-email" /></F>
            <F label="Data de nascimento"><Input type="date" value={form.data_nasc || ""} onChange={(e) => setForm({ ...form, data_nasc: e.target.value })} data-testid="usuario-form-datanasc" /></F>
            <F label={editing ? "Nova senha (opcional)" : "Senha inicial"} req={!editing}>
              <Input type="password" required={!editing} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="usuario-form-senha" placeholder={editing ? "Deixe em branco para manter" : "Mínimo 6 caracteres"} />
            </F>
            <F label="Perfil" req>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="usuario-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div><p className="text-sm font-medium">Ativo</p><p className="text-xs text-muted-foreground">Usuários inativos não conseguem fazer login</p></div>
              <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="usuario-form-submit" className="bg-primary hover:bg-[#3B704E]">{saving ? "Salvando…" : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir usuário?</AlertDialogTitle><AlertDialogDescription>O usuário perderá acesso ao sistema imediatamente.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, req, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}
